/**
 * Die Laufzeitkonfiguration, wie die Oberfläche sie sieht (ADR-044).
 *
 * Erzeugt wird sie von `infra/stacks/portal_konfiguration.py` aus `stack.yaml`,
 * ausgeliefert von Caddy unter `/portal-konfiguration.json`. Dieses Modul
 * beschreibt, was die Oberfläche daraus macht — und zwar **defensiv**.
 *
 * Der Grund ist der Weg dazwischen: Zwischen Erzeuger und Leser liegen eine
 * Datei, ein Webserver und ein Browsercache. Was hier ankommt, ist deshalb
 * `unknown` und kein Typ, den man einfach behauptet. Ein `as PortalKonfiguration`
 * wäre eine Zusicherung über etwas, das man nicht geschrieben hat.
 *
 * **Unbekannte Felder werden ignoriert, fehlende ersetzt.** Eine ältere
 * Oberfläche muss mit einer neueren Konfiguration laufen und umgekehrt — beim
 * Ausrollen liegen die beiden für ein paar Sekunden auseinander, und bei einem
 * Browser mit alter Datei im Cache länger.
 */

import type { Branding } from "./branding.js";
import type { Domains, Merkmale, Stufe } from "./tenant-konfiguration.js";
import { STUFEN } from "./tenant-konfiguration.js";

export interface PortalKonfiguration {
  readonly stackName: string;
  readonly stage: Stufe;
  readonly domains: Partial<Domains>;
  readonly features: Partial<Merkmale>;
  readonly branding: Branding;
  readonly realm: string;
}

/** Was gilt, wenn keine Datei da ist. Bewusst leer statt geraten. */
export const OHNE_KONFIGURATION: PortalKonfiguration = {
  stackName: "",
  stage: "pilot",
  domains: {},
  features: {},
  branding: {},
  realm: "",
};

function text(wert: unknown): string | undefined {
  return typeof wert === "string" && wert.trim() !== "" ? wert : undefined;
}

function objekt(wert: unknown): Record<string, unknown> {
  return typeof wert === "object" && wert !== null && !Array.isArray(wert)
    ? (wert as Record<string, unknown>)
    : {};
}

function nurWahrheitswerte(roh: Record<string, unknown>): Partial<Merkmale> {
  const aus: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(roh)) {
    // Ein Merkmal ist an oder aus. Ein Wert wie "false" als Zeichenkette wäre
    // in JavaScript wahr - und ein abgeschaltetes Merkmal, das erscheint, ist
    // ein Fehlerbild ohne erkennbare Ursache.
    if (typeof v === "boolean") aus[k] = v;
  }
  return aus as Partial<Merkmale>;
}

function nurZeichenketten(roh: Record<string, unknown>): Record<string, string> {
  const aus: Record<string, string> = {};
  for (const [k, v] of Object.entries(roh)) {
    const s = text(v);
    if (s) aus[k] = s;
  }
  return aus;
}

/**
 * Liest, was der Webserver geliefert hat.
 *
 * Wirft nicht. Eine unlesbare Konfiguration darf die Oberfläche nicht
 * mitnehmen — Auflage 3 aus ADR-044: Die Shell startet auch ohne die Datei,
 * mit den Vorgabewerten und einem sichtbaren Hinweis. Ein Portal, das an einer
 * fehlenden Konfigurationsdatei mit einer weißen Seite endet, ist im
 * Störungsfall unbrauchbar — und genau dann braucht man es.
 */
export function lesePortalKonfiguration(roh: unknown): {
  readonly konfiguration: PortalKonfiguration;
  /** Leer, wenn alles Nötige da war. Sonst für den Hinweis in der Shell. */
  readonly fehlend: readonly string[];
} {
  const o = objekt(roh);
  const fehlend: string[] = [];

  const stackName = text(o.stackName);
  if (!stackName) fehlend.push("stackName");

  /*
   * **Ein unbekannter Wert wird gemeldet, nicht umgedeutet.**
   *
   * Hier stand: alles außer `produktion` und `staging` wird `pilot`. Damit
   * verschwand jeder Tippfehler geräuschlos — `produktoin` in der
   * Konfiguration eines Kundenstacks hätte die Oberfläche auf Pilotstufe
   * gestellt, und niemand hätte einen Grund gehabt nachzusehen.
   *
   * Es ist derselbe Fehlertyp, den `04` §4.14 beim Dienstzustand benennt:
   * „Fehlende Information sieht sonst aus wie eine gute Nachricht." Der
   * Rückfall bleibt — die Oberfläche muss laufen —, aber er steht jetzt in
   * `fehlend` und damit im Hinweis der Shell.
   */
  const roheStufe = text(o.stage);
  const stage: Stufe =
    roheStufe !== undefined && (STUFEN as readonly string[]).includes(roheStufe)
      ? (roheStufe as Stufe)
      : OHNE_KONFIGURATION.stage;
  if (roheStufe !== undefined && roheStufe !== stage) fehlend.push("stage");

  const domains = nurZeichenketten(objekt(o.domains)) as Partial<Domains>;
  if (Object.keys(domains).length === 0) fehlend.push("domains");

  const marke = nurZeichenketten(objekt(o.branding)) as Branding;

  return {
    konfiguration: {
      stackName: stackName ?? OHNE_KONFIGURATION.stackName,
      stage,
      domains,
      features: nurWahrheitswerte(objekt(o.features)),
      branding: marke,
      realm: text(o.realm) ?? OHNE_KONFIGURATION.realm,
    },
    fehlend,
  };
}

/**
 * Ist ein Merkmal an?
 *
 * **Ohne Angabe gilt „aus".** Ein Merkmal, das mangels Konfiguration
 * erscheint, ist der schlechtere Fehler: `features.ai` steht nach ADR-017 bis
 * Phase 8 auf `false`, und eine Oberfläche, die den Assistenten zeigt, weil
 * eine Datei fehlte, hält ein Versprechen, das niemand gegeben hat.
 */
export function merkmalAn(
  k: PortalKonfiguration, name: keyof Merkmale,
): boolean {
  return k.features[name] === true;
}
